import React, {useCallback, useContext, useEffect, useReducer, useRef, useState} from 'react';
import {
	Card,
	CardContent,
	CardHeader,
	Collapse,
	Container,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField
} from "@mui/material";
import {BaseStyle} from "../../../styles/shared/BaseStyle";
import clsx from "clsx";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import {ContattaciStyle} from "../../../styles/pages/contact/ContattaciStyle";
import TamponiDrawer from "./drawer/TamponiDrawer";
import HideAppBar from "../../widgets/header/HideAppBar";
import useTamponiSearch from "./utils/useTamponiSearch";
import { Alert, AlertTitle, Skeleton } from '@mui/material';
import useGetAllTamponi from "./utils/useGetAllTamponi";
import TamponeCard from "./details/TamponeCard";
import {useHistory} from "react-router-dom";
import {AUTOMATION_INITIAL_STATE, AutomationReducer, initAutomationState} from "../../../reducers/AutomationReducer";
import {getAccountInfo} from "../../../utils/GenericUtils";
import {setOpenSnackbar} from "../../../services/AppServices";
import {AppContext} from "../../../App";
import FilterListTwoToneIcon from "@mui/icons-material/FilterListTwoTone";
import {onClickDetails} from "../../../services/AutomationServices";
import {useSessionStorage} from "../../../utils/hooks/SessionStorageHook";
import {KeyboardDatePickerCompat} from "../../widgets/inputs/KeyboardDatePickerCompat";


const GestioneTamponi = () => {
	const classes = BaseStyle();
	const contattaciClasses = ContattaciStyle();
	const appContext = useContext(AppContext);
	const [automationState, automationDispatch] = useReducer(AutomationReducer, AUTOMATION_INITIAL_STATE, initAutomationState);
	const [stato, setStato] = useSessionStorage("stato_tmp", "Da gestire");
	const [open, setOpen] = useState(false);
	const [testTipoDesc, setTestTipoDesc] = useState("tutti");
	const [value, setValue] = useState("");
	const [filters, setFilters] = useState("");
	const [irreperibili, setIrreperibili] = useState("");
	const [pageNumber, setPageNumber] = useState(0);
	const [sort, setSort] = useSessionStorage("sort_tamp", "dataEsito");
	const [dir, setDir] = useSessionStorage("dir_tamp", "ASC");
	const [dataEsito, setDataEsito] = useSessionStorage("dataEsito_tamp", null);
	const {
		loading,
		error,
		hasMore,
		tamponi,
		info
	} = useGetAllTamponi(stato, filters, pageNumber, testTipoDesc, sort, dir, irreperibili, dataEsito);
	const {loadingSearch, errorSearch, hasMoreSearch, tamponiSearch} = useTamponiSearch(value, pageNumber)
	const observer = useRef();
	const history = useHistory();
	const [selected, setSelected] = useState("")
	
	const lastTicketElementRef = useCallback(node => {
		if (loading) return
		if (observer.current) observer.current.disconnect()
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMore) {
				setPageNumber(prevState => prevState + 1);
			}
			if (entries[0].isIntersecting && hasMoreSearch) {
				setPageNumber(prevState => prevState + 1);
			}
		})
		if (node) observer.current.observe(node)
	}, [loading, hasMore, hasMoreSearch])
	
	const onClickTampone = useCallback((tampone) => {
		setSelected(tampone);
		onClickDetails(tampone.id, "tampone", "modify", automationDispatch);
	}, [automationDispatch])
	
	const setMenuSelected = useCallback((newValue) => {
		setPageNumber(0);
		setStato(newValue);
	}, [setStato]);
	
	useEffect(() => {
		if (!automationState.loadingOnClick && automationState.errorOnClick === "") {
			if (automationState.operatorOnClick.replace("ERR_", "").includes(getAccountInfo().alias)) {
				history.push("/gestione/interviste/" + selected.id);
			} else {
				setOpenSnackbar("warning", ("Il tampone è al momento gestito da " + automationState.operatorOnClick?.replace("ERR_", "") + ". Ritenta e sari più fortunato :)"), appContext.dispatch)
			}
		}
	}, [automationState.loadingOnClick, automationState.errorOnClick, automationState.operatorOnClick, selected, history, appContext.dispatch])
	
	useEffect(() => {
		setPageNumber(0)
	}, [filters, testTipoDesc, sort, dir, irreperibili, dataEsito])
	
	useEffect(() => {
		document.body.style = `background: none;`;
	}, []);
	
	return (
        <React.Fragment>
            <main className={classes.main}>
				<HideAppBar title={"Tamponi molecolari"}/>
				<Container maxWidth={"xl"} component={"article"}
				           style={{paddingTop: 12}}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Paper className={clsx(classes.filterSearchPaper, classes.roundBorder)} component={"section"}>
								<TextField
									placeholder={"Cerca per Codice fiscale, esito e molto altro"}
									value={value}
									onChange={(e) => setValue(e.target.value)}
									autoFocus={false}
									fullWidth={true}
									variant={"outlined"}
									InputProps={{
										startAdornment: <InputAdornment position="start">
											<Tooltip title={"Cerca"}>
												<IconButton color="inherit" size="large">
													<SearchTwoToneIcon/>
												</IconButton>
											</Tooltip>
										</InputAdornment>,
										endAdornment: <InputAdornment position="start">
											<Tooltip title={"Ricerca avanzata"}>
												<IconButton
                                                    color="inherit"
                                                    onClick={() => setOpen(!open)}
                                                    className={clsx(classes.expand, {
                                                        [classes.expandOpen]: open,
                                                    })}
                                                    size="large">
													<FilterListTwoToneIcon/>
												</IconButton>
											</Tooltip>
										</InputAdornment>,
										classes: {notchedOutline: contattaciClasses.noBorder},
									}}
								/>
								<Collapse in={open} timeout="auto" unmountOnExit>
									<Grid container spacing={3} className={classes.padding2}>
										<Grid item xs={12} sm={6}>
											<FormControl variant="outlined" fullWidth>
												<InputLabel id="sort">Ordina per</InputLabel>
												<Select
													labelId="sort"
													id="sort"
													value={sort}
													onChange={(e) => setSort(e.target.value)}
													label="Ordina per"
												>
													<MenuItem value={"dataEsito"}>Data esito</MenuItem>
													<MenuItem value={"dataCreazione"}>Data creazione</MenuItem>
													<MenuItem value={"dataPrelievoEffettiva"}>Data prelievo effettiva</MenuItem>
													<MenuItem value={"dataPrelievoPrevista"}>Data prelievo prevista</MenuItem>
													<MenuItem value={"dataPrimaModifica"}>Data prima modifica</MenuItem>
													<MenuItem value={"dataUltimaModifica"}>Data ultima modifica</MenuItem>
													<MenuItem value={"indirizzoAssociato"}>Indirizzo associato</MenuItem>
													<MenuItem value={"laboratorio"}>Laboratorio</MenuItem>
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControl variant="outlined" fullWidth>
												<InputLabel id="dir">Ascendente / Discendente</InputLabel>
												<Select
													labelId="dir"
													id="dir"
													value={dir}
													onChange={(e) => setDir(e.target.value)}
													label="Ascendente / Discendente"
												>
													<MenuItem value={"ASC"}>Dal più piccolo al più grande</MenuItem>
													<MenuItem value={"DESC"}>Dal più grande al più piccolo</MenuItem>
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} sm={8}>
											<FormControl variant="outlined" fullWidth={true}>
												<InputLabel id="demo-simple-select-outlined-label">Test tipo desc</InputLabel>
												<Select
													fullWidth={true}
													labelId="demo-simple-select-outlined-label"
													id="demo-simple-select-outlined"
													value={testTipoDesc}
													onChange={event => setTestTipoDesc(event.target.value)}
													label="Test tipo desc"
												>
													<MenuItem value={"tutti"}>Tutti i tipi di tampone</MenuItem>
													<MenuItem value={"Tampone molecolare (91.12.S)"}>Tampone molecolare (91.12.S)</MenuItem>
													<MenuItem value={"Tampone molecolare salivare (91.12.X)"}>Tampone molecolare salivare
														(91.12.X)</MenuItem>
													<MenuItem value={"Tampone Rapido a lettura facilitata (91.13.S)"}>Tampone Rapido a lettura
														facilitata (91.13.S)</MenuItem>
													<MenuItem value={"Tampone Rapido a lettura manuale (91.13.M)"}>Tampone Rapido a lettura
														manuale (91.13.M)</MenuItem>
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} sm={4}>
											<KeyboardDatePickerCompat
													margin="normal"
													id="date-picker-dialog"
													label="Data"
													inputVariant="outlined"
													format="dd/MM/yyyy"
													value={dataEsito}
													onChange={setDataEsito}
													style={{
														width: "100%",
														marginTop: 0
													}}
													KeyboardButtonProps={{
														'aria-label': 'change date',
													}}
												/>
										</Grid>
									</Grid>
								</Collapse>
							</Paper>
						</Grid>
						
						<Grid item xs={12}>
							<Card className={clsx(classes.roundBorder)}
							      style={{height: "100%", display: "flex", flexDirection: "column"}}>
								<CardContent style={{flexGrow: 1, display: "flex"}}>
									
									<TamponiDrawer selectMenuItem={setMenuSelected} stato={stato} setFilters={setFilters}
									               filters={filters} info={info} irreperibili={irreperibili}
									               setIrreperibili={setIrreperibili}/>
									
									<div className={classes.content}>
										{
											(value === "" ? tamponi : tamponiSearch).map((tampone, index) => {
												if (tamponi.length === index + 1) {
													return (
														<Card ref={lastTicketElementRef} key={tampone.id}
														      className={clsx(classes.roundBorder, classes.mailCard)}>
															<TamponeCard tampone={tampone} onClickTampone={onClickTampone} setValue={setValue}/>
														</Card>
													)
												} else {
													return (
														<Card key={tampone.id} className={clsx(classes.roundBorder, classes.mailCard)}>
															<TamponeCard tampone={tampone} onClickTampone={onClickTampone} setValue={setValue}/>
														</Card>
													)
												}
											})
										}
										{value === "" && !loading && !error && tamponi.length === 0 &&
											<Alert severity="info" style={{borderRadius: 24}}>
												<AlertTitle>Evvai 😇!!</AlertTitle>
												Non ci sono tamponi! Goditi questo meritato relax
											</Alert>}
										{value === "" && loading &&
											<Card className={clsx(classes.roundBorder, classes.mailCard)} elevation={0}>
												<CardHeader
													avatar={<Skeleton variant="circular" height={40} width={40}/>}
													title={<Skeleton width={180}/>}
													subheader={<Skeleton width={260}/>}
													action={<Skeleton variant="circular" height={20} width={20}/>}
													style={{paddingBottom: 2}}
												/>
												<CardContent style={{paddingTop: 0, marginTop: -35}}>
													<Skeleton height={200}/>
												</CardContent>
											</Card>}
										{value === "" && error && !loading && <Alert severity="error" style={{borderRadius: 24}}>
											<AlertTitle>Errore</AlertTitle>
											Prova ad effettuare un refresh della pagina entro 5 minuti. Se l'errore persiste contatta gli
											sviluppatori.
										</Alert>}
										
										{value !== "" && !loadingSearch && tamponiSearch.length === 0 &&
											<Alert severity="info" style={{borderRadius: 24}}>
												<AlertTitle>Evvai 😇!!</AlertTitle>
												Non ci sono tamponi! Goditi questo meritato relax
											</Alert>}
										{value !== "" && loadingSearch &&
											<Card className={clsx(classes.roundBorder, classes.mailCard)} elevation={0}>
												<CardHeader
													avatar={<Skeleton variant="circular" height={40} width={40}/>}
													title={<Skeleton width={180}/>}
													subheader={<Skeleton width={260}/>}
													action={<Skeleton variant="circular" height={20} width={20}/>}
													style={{paddingBottom: 2}}
												/>
												<CardContent style={{paddingTop: 0, marginTop: -35}}>
													<Skeleton height={200}/>
												</CardContent>
											</Card>}
										{value !== "" && errorSearch && !loadingSearch &&
											<Alert severity="error" style={{borderRadius: 24}}>
												<AlertTitle>Errore ricerca</AlertTitle>
												Prova ad effettuare un refresh della pagina entro 5 minuti. Se l'errore persiste contatta gli
												sviluppatori.
											</Alert>}
									
									
									</div>
								
								</CardContent>
							</Card>
						</Grid>
					
					</Grid>
				</Container>
			</main>
        </React.Fragment>
    );
}

export default React.memo(GestioneTamponi);
